// src/components/common/ScoreEntryModal.js

import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { getMatchElapsedTime, formatMinutes } from "../../utils/matchTimingUtils";

export default function ScoreEntryModal({
    match,
    onClose,
    onSubmit,
    onUndo,
    onUnstart,
    readOnly = false,
    raceTarget = null // Max games to win (race-to N). Caps entry + auto-picks winner.
}) {
    const [selectedWinner, setSelectedWinner] = useState(null);
    const [player1Score, setPlayer1Score] = useState(match?.score1 || "0");
    const [player2Score, setPlayer2Score] = useState(match?.score2 || "0");
    const [, setCurrentTime] = useState(Date.now()); // Used to trigger re-renders for live elapsed time

    useEffect(() => {
        if (match) {
            setPlayer1Score(match.score1 || "0");
            setPlayer2Score(match.score2 || "0");

            // Pre-select winner if match is finished
            if (match.finished && match.winner) {
                setSelectedWinner(match.winner);
            } else {
                setSelectedWinner(null);
            }
        }
    }, [match]);

    // Update current time every second for live elapsed time display
    useEffect(() => {
        if (match?.started && !match?.finished) {
            const interval = setInterval(() => {
                setCurrentTime(Date.now());
            }, 1000);

            return () => clearInterval(interval);
        }
    }, [match?.started, match?.finished]);

    // Auto-highlight the winner the instant a player reaches the race target, so
    // the director can just tap + up to the winning game and submit. Only ever
    // auto-selects (never clears), so a manual pick — or the 0-0 "just choose a
    // winner" flow — is preserved.
    useEffect(() => {
        if (!raceTarget || raceTarget <= 0 || !match || match.finished) return;
        const s1 = Number(player1Score);
        const s2 = Number(player2Score);
        const real = (n) => n && n !== 'BYE' && !n.includes('Winner of') && !n.includes('Loser of');
        if (s1 >= raceTarget && s1 > s2 && real(match.p1?.name)) setSelectedWinner(match.p1.name);
        else if (s2 >= raceTarget && s2 > s1 && real(match.p2?.name)) setSelectedWinner(match.p2.name);
    }, [player1Score, player2Score, raceTarget, match]);

    if (!match) return null;

    // Cap manual entry at the race target (a player can't win more games than the
    // race is to). Null when unknown → no cap (legacy behavior).
    const scoreCap = raceTarget && raceTarget > 0 ? raceTarget : null;
    const clampScore = (n) => {
        if (n < 0) return 0;
        if (scoreCap && n > scoreCap) return scoreCap;
        return n;
    };

    const elapsedMinutes = match.started && !match.finished ? getMatchElapsedTime(match) : null;

    const handleSubmit = () => {
        if (!selectedWinner) {
            alert("Please select a winner");
            return;
        }

        const score1 = Number(player1Score);
        const score2 = Number(player2Score);

        if (isNaN(score1) || isNaN(score2)) {
            alert("Please enter valid scores");
            return;
        }

        // Allow 0-0 scores (no score entry, just winner selection)
        // Only validate scores if at least one score is greater than 0
        if (score1 > 0 || score2 > 0) {
            if (score1 === score2) {
                alert("Match cannot end in a tie. Please enter different scores.");
                return;
            }

            // Validate that the selected winner has the higher score
            if (selectedWinner === match.p1.name && score1 <= score2) {
                alert(`${match.p1.name} is selected as winner but has a lower or equal score`);
                return;
            }

            if (selectedWinner === match.p2.name && score2 <= score1) {
                alert(`${match.p2.name} is selected as winner but has a lower or equal score`);
                return;
            }
        }

        onSubmit({
            matchId: match.id,
            score1: player1Score,
            score2: player2Score,
            winner: selectedWinner
        });

        onClose();
    };

    const handleUndo = () => {
        if (onUndo) {
            onUndo(match);
            onClose();
        }
    };

    const handleUnstart = () => {
        if (onUnstart) {
            onUnstart(match.id);
            onClose();
        }
    };

    const handlePlayerClick = (playerName) => {
        if (playerName === "BYE" || !playerName ||
            playerName.includes("Winner of") ||
            playerName.includes("Loser of")) {
            return;
        }
        setSelectedWinner(playerName);
    };

    const isPlayer1Disabled = match.finished || match.p1.name === "BYE" || !match.p1.name ||
        match.p1.name.includes("Winner of") ||
        match.p1.name.includes("Loser of");

    const isPlayer2Disabled = match.finished || match.p2.name === "BYE" || !match.p2.name ||
        match.p2.name.includes("Winner of") ||
        match.p2.name.includes("Loser of");

    // Use a portal to render the modal at the document body level
    // This ensures the modal is always centered in the viewport,
    // even when rendered inside a transformed container (like a panned/zoomed bracket)
    return ReactDOM.createPortal(
        <div
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'var(--bg-modal-overlay)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10000
            }}
            onClick={onClose}
        >
            <div
                style={{
                    backgroundColor: 'var(--bg-secondary)',
                    borderRadius: 8,
                    padding: 24,
                    maxWidth: 400,
                    width: '90%',
                    boxShadow: 'var(--shadow-lg)'
                }}
                onClick={(e) => e.stopPropagation()}
            >
                <h3 style={{ marginTop: 0, marginBottom: 16, fontSize: 18, fontWeight: 'bold', color: 'var(--text-primary)' }}>
                    Update Match Score
                </h3>

                <div style={{ marginBottom: 12, fontSize: 14, color: 'var(--text-secondary)' }}>
                    Match #{match.matchNum}
                    {scoreCap ? ` · Race to ${scoreCap}` : ''}
                    {elapsedMinutes !== null && (
                        <div style={{
                            marginTop: 8,
                            fontSize: 16,
                            fontWeight: 'bold',
                            color: 'var(--btn-primary)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6
                        }}>
                            <span>⏱️</span>
                            <span>Match Time: {formatMinutes(Math.round(elapsedMinutes))}</span>
                        </div>
                    )}
                </div>

                {/* Player 1 */}
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: 12,
                        marginBottom: 8,
                        border: `2px solid ${selectedWinner === match.p1.name ? 'var(--success)' : 'var(--border-color)'}`,
                        borderRadius: 6,
                        backgroundColor: selectedWinner === match.p1.name ? 'var(--success-bg)' :
                                       selectedWinner === match.p2.name ? 'var(--bg-tertiary)' : 'var(--bg-secondary)',
                        opacity: isPlayer1Disabled ? 0.5 : 1
                    }}
                >
                    {/* Only the name area selects the winner — clicking the score
                        controls (or the padding/fringe around them) must not. */}
                    <div
                        onClick={isPlayer1Disabled ? undefined : () => handlePlayerClick(match.p1.name)}
                        title={isPlayer1Disabled ? undefined : 'Click to select as winner'}
                        style={{
                            flex: 1,
                            alignSelf: 'stretch',
                            display: 'flex',
                            alignItems: 'center',
                            fontWeight: selectedWinner === match.p1.name ? 'bold' : 'normal',
                            color: 'var(--text-primary)',
                            cursor: isPlayer1Disabled ? 'default' : 'pointer',
                            padding: '6px 4px',
                            borderRadius: 4
                        }}
                    >
                        {match.p1.name || "TBD"}
                    </div>
                    <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                setPlayer1Score(String(Math.max(0, Number(player1Score) - 1)));
                            }}
                            disabled={isPlayer1Disabled}
                            style={{
                                width: 40,
                                height: 40,
                                padding: 0,
                                fontSize: 22,
                                fontWeight: 'bold',
                                border: '1px solid var(--border-color)',
                                borderRadius: 4,
                                backgroundColor: isPlayer1Disabled ? 'var(--bg-tertiary)' : 'var(--bg-secondary)',
                                color: 'var(--text-primary)',
                                cursor: isPlayer1Disabled ? 'default' : 'pointer'
                            }}
                        >
                            −
                        </button>
                        <input
                            type="number"
                            min="0"
                            max={scoreCap || undefined}
                            value={player1Score}
                            onChange={(e) => {
                                const raw = e.target.value;
                                if (raw === '') { setPlayer1Score(''); return; }
                                const n = Number(raw);
                                if (isNaN(n)) return;
                                setPlayer1Score(String(clampScore(n)));
                            }}
                            onClick={(e) => e.stopPropagation()}
                            style={{
                                width: 44,
                                padding: '6px 4px',
                                fontSize: 18,
                                textAlign: 'center',
                                border: '1px solid var(--border-color)',
                                borderRadius: 4,
                                backgroundColor: 'var(--bg-secondary)',
                                color: 'var(--text-primary)'
                            }}
                            disabled={isPlayer1Disabled}
                        />
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                setPlayer1Score(String(clampScore(Number(player1Score) + 1)));
                            }}
                            disabled={isPlayer1Disabled || (scoreCap && Number(player1Score) >= scoreCap)}
                            style={{
                                width: 40,
                                height: 40,
                                padding: 0,
                                fontSize: 22,
                                fontWeight: 'bold',
                                border: '1px solid var(--border-color)',
                                borderRadius: 4,
                                backgroundColor: isPlayer1Disabled ? 'var(--bg-tertiary)' : 'var(--bg-secondary)',
                                color: 'var(--text-primary)',
                                cursor: isPlayer1Disabled ? 'default' : 'pointer'
                            }}
                        >
                            +
                        </button>
                    </div>
                </div>

                {/* Player 2 */}
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: 12,
                        marginBottom: 20,
                        border: `2px solid ${selectedWinner === match.p2.name ? 'var(--success)' : 'var(--border-color)'}`,
                        borderRadius: 6,
                        backgroundColor: selectedWinner === match.p2.name ? 'var(--success-bg)' :
                                       selectedWinner === match.p1.name ? 'var(--bg-tertiary)' : 'var(--bg-secondary)',
                        opacity: isPlayer2Disabled ? 0.5 : 1
                    }}
                >
                    {/* Only the name area selects the winner — clicking the score
                        controls (or the padding/fringe around them) must not. */}
                    <div
                        onClick={isPlayer2Disabled ? undefined : () => handlePlayerClick(match.p2.name)}
                        title={isPlayer2Disabled ? undefined : 'Click to select as winner'}
                        style={{
                            flex: 1,
                            alignSelf: 'stretch',
                            display: 'flex',
                            alignItems: 'center',
                            fontWeight: selectedWinner === match.p2.name ? 'bold' : 'normal',
                            color: 'var(--text-primary)',
                            cursor: isPlayer2Disabled ? 'default' : 'pointer',
                            padding: '6px 4px',
                            borderRadius: 4
                        }}
                    >
                        {match.p2.name || "TBD"}
                    </div>
                    <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                setPlayer2Score(String(Math.max(0, Number(player2Score) - 1)));
                            }}
                            disabled={isPlayer2Disabled}
                            style={{
                                width: 40,
                                height: 40,
                                padding: 0,
                                fontSize: 22,
                                fontWeight: 'bold',
                                border: '1px solid var(--border-color)',
                                borderRadius: 4,
                                backgroundColor: isPlayer2Disabled ? 'var(--bg-tertiary)' : 'var(--bg-secondary)',
                                color: 'var(--text-primary)',
                                cursor: isPlayer2Disabled ? 'default' : 'pointer'
                            }}
                        >
                            −
                        </button>
                        <input
                            type="number"
                            min="0"
                            max={scoreCap || undefined}
                            value={player2Score}
                            onChange={(e) => {
                                const raw = e.target.value;
                                if (raw === '') { setPlayer2Score(''); return; }
                                const n = Number(raw);
                                if (isNaN(n)) return;
                                setPlayer2Score(String(clampScore(n)));
                            }}
                            onClick={(e) => e.stopPropagation()}
                            style={{
                                width: 44,
                                padding: '6px 4px',
                                fontSize: 18,
                                textAlign: 'center',
                                border: '1px solid var(--border-color)',
                                borderRadius: 4,
                                backgroundColor: 'var(--bg-secondary)',
                                color: 'var(--text-primary)'
                            }}
                            disabled={isPlayer2Disabled}
                        />
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                setPlayer2Score(String(clampScore(Number(player2Score) + 1)));
                            }}
                            disabled={isPlayer2Disabled || (scoreCap && Number(player2Score) >= scoreCap)}
                            style={{
                                width: 40,
                                height: 40,
                                padding: 0,
                                fontSize: 22,
                                fontWeight: 'bold',
                                border: '1px solid var(--border-color)',
                                borderRadius: 4,
                                backgroundColor: isPlayer2Disabled ? 'var(--bg-tertiary)' : 'var(--bg-secondary)',
                                color: 'var(--text-primary)',
                                cursor: isPlayer2Disabled ? 'default' : 'pointer'
                            }}
                        >
                            +
                        </button>
                    </div>
                </div>

                <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 16, fontStyle: 'italic' }}>
                    Click a player's name to select them as the winner
                </div>

                {/* Action buttons */}
                <div style={{ display: 'flex', gap: 8, justifyContent: (match.finished || match.started) && !readOnly ? 'space-between' : 'flex-end' }}>
                    {match.finished && !readOnly && (
                        <button
                            onClick={handleUndo}
                            style={{
                                padding: '8px 16px',
                                fontSize: 14,
                                border: 'none',
                                borderRadius: 4,
                                backgroundColor: 'var(--danger)',
                                color: 'var(--bg-secondary)',
                                cursor: 'pointer',
                                fontWeight: 'bold'
                            }}
                        >
                            Undo Match
                        </button>
                    )}
                    {match.started && !match.finished && !readOnly && (
                        <button
                            onClick={handleUnstart}
                            style={{
                                padding: '8px 16px',
                                fontSize: 14,
                                border: 'none',
                                borderRadius: 4,
                                backgroundColor: '#ffc107',
                                color: '#000',
                                cursor: 'pointer',
                                fontWeight: 'bold'
                            }}
                        >
                            Unstart Match
                        </button>
                    )}
                    <div style={{ display: 'flex', gap: 8 }}>
                        <button
                            onClick={onClose}
                            style={{
                                padding: '8px 16px',
                                fontSize: 14,
                                border: '1px solid var(--border-color)',
                                borderRadius: 4,
                                backgroundColor: 'var(--bg-secondary)',
                                color: 'var(--text-primary)',
                                cursor: 'pointer'
                            }}
                        >
                            {match.finished ? 'Close' : 'Cancel'}
                        </button>
                        {!match.finished && !readOnly && (
                            <button
                                onClick={handleSubmit}
                                style={{
                                    padding: '8px 16px',
                                    fontSize: 14,
                                    border: 'none',
                                    borderRadius: 4,
                                    backgroundColor: 'var(--success)',
                                    color: 'var(--bg-secondary)',
                                    cursor: 'pointer',
                                    fontWeight: 'bold'
                                }}
                            >
                                Update Score
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
}
